import { BinIcon } from '@systemeio/ui/icons'
import { Checkbox, NeutralButton, NeutralTypeEnum, TimePicker } from '@systemeio/ui'
import {
  getWeekdayShortLocoKey,
  sortWeekdaySlots,
} from 'modules/calendar/utils/date-time-formatters'
import ButtonLikeLink from 'shared/components/button-like-link'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { twJoin, twMerge } from 'tailwind-merge'
import {
  AvailabilitySlot,
  AvailabilitySlotId,
  WeekdaysBackendEnum,
} from '../types/availability-interface'
import { AvailabilitySlotsSkeleton } from './availability-slots-skeleton'
import { PlusIcon } from '@systemeio/ui/icons'

interface WeekdaysAvailabilitySlotsProps {
  slots: AvailabilitySlot[]
  addSlot: (day: Omit<AvailabilitySlot, 'id'>) => void
  updateSlot: (day: AvailabilitySlot) => void
  removeSlots: (slotIds: AvailabilitySlotId[]) => void
  isLoading?: boolean
  disabled?: boolean
  titleClassName?: string
}

export function WeekdaysAvailabilitySlots({
  slots,
  addSlot,
  updateSlot,
  removeSlots,
  isLoading,
  disabled,
  titleClassName,
}: WeekdaysAvailabilitySlotsProps) {
  const { t } = useLocoTranslation()

  return (
    <>
      <p className={twMerge('mb-5', titleClassName)}>
        {t('dashboard.calendar.weekly_hours_availability')}
      </p>
      <div className="flex flex-col">
        {isLoading && <AvailabilitySlotsSkeleton />}
        {!isLoading &&
          Object.values(WeekdaysBackendEnum).map(weekday => {
            const weekdaySlots = slots.filter(({ dayOfWeek }) => dayOfWeek === weekday)
            const isAvailable = weekdaySlots.length > 0

            return (
              <div
                key={weekday}
                className="border-text-secondary/30 grid grid-cols-12 items-start border-t py-5"
              >
                <div
                  className={twJoin(
                    'col-span-4 lg:col-span-2',
                    weekdaySlots.length > 1 ? 'self-start' : 'self-center',
                  )}
                >
                  <Checkbox
                    value={isAvailable}
                    label={t(getWeekdayShortLocoKey(weekday))}
                    onChange={() => {
                      if (isAvailable) {
                        removeSlots(weekdaySlots.map(({ id }) => id))
                        return
                      }
                      addSlot({
                        dayOfWeek: weekday,
                        startTime: null,
                        endTime: null,
                        date: null,
                      })
                    }}
                    disabled={disabled}
                  />
                </div>

                <div className="col-span-8 lg:col-span-10">
                  {isAvailable ? (
                    <div className="grid grid-cols-[1fr_minmax(20px,50px)] items-center gap-2 lg:grid-cols-[1fr_160px]">
                      {sortWeekdaySlots(weekdaySlots).map((slot, i) => (
                        <WeekdaySlot
                          key={i}
                          weekdaySlot={slot}
                          addSlot={addSlot}
                          removeSlots={removeSlots}
                          updateSlot={updateSlot}
                          slotIndex={i}
                          weekday={weekday}
                          disabled={disabled}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 items-center gap-2">
                      <span className="col-span-2">
                        {t('dashboard.calendar.time_slot_unavailable')}
                      </span>
                      <ButtonLikeLink
                        type="button"
                        className="col-span-2 ml-auto flex items-center gap-1 py-2 text-sm"
                        disabled
                      >
                        <PlusIcon fill="gray" />
                        <span className="hidden lg:block">
                          {t('dashboard.calendar.add_time_slot')}
                        </span>
                      </ButtonLikeLink>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
      </div>
    </>
  )
}

interface WeekdaySlotProps extends Omit<WeekdaysAvailabilitySlotsProps, 'slots' | 'isLoading'> {
  weekdaySlot: AvailabilitySlot
  slotIndex: number
  weekday: WeekdaysBackendEnum
  disabled?: boolean
}

function WeekdaySlot({
  weekdaySlot,
  addSlot,
  removeSlots,
  updateSlot,
  slotIndex,
  weekday,
  disabled,
}: WeekdaySlotProps) {
  const { t } = useLocoTranslation()
  const { user } = useUser()

  return (
    <>
      <div className="xxl:gap-5 flex flex-1 flex-row flex-wrap gap-1">
        <TimePicker
          seconds={weekdaySlot.startTime}
          onChangeSeconds={seconds => {
            updateSlot({ ...weekdaySlot, startTime: seconds })
          }}
          disabled={disabled}
          wrapperClassName="w-[115px] sm:w-[128px]"
          classNameCustomInput="w-full"
          userDateFormat={user?.dateFormat}
          userTimeFormat={user?.timeFormat}
        />
        <TimePicker
          seconds={weekdaySlot.endTime}
          onChangeSeconds={seconds => {
            updateSlot({ ...weekdaySlot, endTime: seconds })
          }}
          disabled={disabled}
          wrapperClassName="w-[115px] sm:w-[128px]"
          classNameCustomInput="w-full"
          userDateFormat={user?.dateFormat}
          userTimeFormat={user?.timeFormat}
        />
      </div>
      <div className="ml-auto">
        {slotIndex === 0 ? (
          <ButtonLikeLink
            type="button"
            className="ml-auto flex gap-1 pl-0 text-sm md:pl-4"
            onClick={() => {
              addSlot({
                dayOfWeek: weekday,
                startTime: null,
                endTime: null,
                date: null,
              })
            }}
            disabled={disabled}
          >
            <PlusIcon fill={disabled ? 'gray' : 'rgb(var(--brand-primary))'} />
            <span className="hidden lg:block">{t('dashboard.calendar.add_time_slot')}</span>
          </ButtonLikeLink>
        ) : (
          <NeutralButton
            type="button"
            neutralType={NeutralTypeEnum.danger}
            onClick={() => removeSlots([weekdaySlot.id])}
            disabled={disabled}
          >
            <BinIcon
              width={16}
              height={16}
              className="fill-text-primary group-hover:fill-danger transition-all "
            />
            <span className="hidden lg:block">{t('global.remove')}</span>
          </NeutralButton>
        )}
      </div>
    </>
  )
}
